import React, { useEffect, useState } from "react";
import { Box, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Instagram-style image carousel for a multi-image post. Shows prev/next
// arrows and dot indicators only when there's more than one image. The parent
// controls the frame; `imgStyle` tunes the image sizing per surface.
export default function ImageCarousel({ images = [], alt = "", imgStyle = {} }) {
  const list = (images || []).filter((im) => im && im.filePath);
  const [idx, setIdx] = useState(0);

  // Reset to the first image when the set of images changes.
  useEffect(() => {
    setIdx(0);
  }, [list.length]);

  if (list.length === 0) return null;

  const safeIdx = Math.min(idx, list.length - 1);
  const current = list[safeIdx];
  const go = (e, delta) => {
    e.stopPropagation();
    setIdx((i) => (i + delta + list.length) % list.length);
  };

  return (
    <Box sx={{ position: "relative", width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <img
        src={current.filePath}
        alt={alt}
        style={{ width: "100%", maxHeight: "82vh", objectFit: "contain", display: "block", ...imgStyle }}
      />

      {list.length > 1 && (
        <>
          <IconButton
            onClick={(e) => go(e, -1)}
            size="small"
            sx={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "rgba(0,0,0,0.5)",
              color: "#fff",
              "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={(e) => go(e, 1)}
            size="small"
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "rgba(0,0,0,0.5)",
              color: "#fff",
              "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
            }}
          >
            <ChevronRightIcon />
          </IconButton>

          {/* Dots */}
          <Box
            sx={{
              position: "absolute",
              bottom: 10,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: 0.75,
            }}
          >
            {list.map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: i === safeIdx ? "#fff" : "rgba(255,255,255,0.45)",
                }}
              />
            ))}
          </Box>

          {/* Counter */}
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              px: 1,
              py: 0.25,
              borderRadius: "10px",
              bgcolor: "rgba(0,0,0,0.55)",
              color: "#fff",
              fontSize: 12,
            }}
          >
            {safeIdx + 1}/{list.length}
          </Box>
        </>
      )}
    </Box>
  );
}
